package com.arena_web.data.http

import com.arena_web.core.config.AppConfig
import com.arena_web.core.config.Environment
import com.arena_web.core.logger.getLogger
import com.arena_web.data.dto.RefreshTokenRequest
import com.arena_web.data.dto.RefreshTokenResponse
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withTimeout
import kotlinx.serialization.json.Json

const val PLATFORM_HEADER = "X-Platform"
const val PLATFORM_VALUE = "web"

object HttpClientFactory {
    private val logger = getLogger<HttpClientFactory>()

    private val jsonConfig = Json {
        ignoreUnknownKeys = true
        isLenient = true
        encodeDefaults = true
        explicitNulls = false
        prettyPrint = AppConfig.environment == Environment.DEVELOPMENT
    }

    fun createDefault(): HttpClient {
        return HttpClient(Js) {
            install(ContentNegotiation) { json(jsonConfig) }
            installLogging()
            defaultRequest {
                header(PLATFORM_HEADER, PLATFORM_VALUE)
                url { parameters.append("_", cacheBuster()) }
            }
            expectSuccess = false
        }
    }

    fun createAuthenticated(token: String): HttpClient {
        val client = HttpClient(Js) {
            install(ContentNegotiation) { json(jsonConfig) }
            install(HttpSend) { maxSendCount = 2 }
            installLogging()

            defaultRequest {
                header("Content-Type", "application/json")
                header("Accept", "application/json")
                header(PLATFORM_HEADER, PLATFORM_VALUE)
                url { parameters.append("_", cacheBuster()) }
                val currentToken = Storage.get(StorageKeys.ACCESS_TOKEN) ?: token
                header("Authorization", "Bearer $currentToken")
            }

            expectSuccess = false
        }

        client.plugin(HttpSend).intercept { request ->
            val originalCall = execute(request)
            if (originalCall.response.status == HttpStatusCode.Unauthorized) {
                val staleToken = request.headers[HttpHeaders.Authorization]?.removePrefix("Bearer ")
                val newToken = tryRefreshToken(staleToken)
                if (newToken != null) {
                    request.headers.remove(HttpHeaders.Authorization)
                    request.headers.append(HttpHeaders.Authorization, "Bearer $newToken")
                    execute(request)
                } else {
                    originalCall
                }
            } else {
                originalCall
            }
        }

        return client
    }

    /** Bounds the refresh call so a hung request can't jam [refreshMutex]
     *  and freeze all 401 recovery until the page is reloaded. */
    private const val REFRESH_TIMEOUT_MS = 30_000L

    /** Serializes all refresh attempts — parallel 401 handlers used to each fire
     *  their own POST /auth/refresh, racing the backend's token rotation (the
     *  winner rotates, the rest hit "revoked") and burning the rate limit. */
    private val refreshMutex = Mutex()

    /**
     * Public entry for the WebSocket clients: run their pre-(re)connect token
     * refresh through the same single-flight mutex as the 401 interceptor.
     * [staleToken] is the token the failed attempt used (see [tryRefreshToken]).
     */
    suspend fun ensureFreshToken(staleToken: String?): String? = tryRefreshToken(staleToken)

    /**
     * Single-flight token refresh — mirrors the mobile HttpClientFactory.
     * [staleToken] is the access token the failed request was sent with: if the
     * stored token already differs by the time we get the lock, another caller
     * has just refreshed — reuse its result instead of firing one more request.
     */
    private suspend fun tryRefreshToken(staleToken: String?): String? = refreshMutex.withLock {
        Storage.get(StorageKeys.ACCESS_TOKEN)
            ?.takeIf { it != staleToken }
            ?.let { return@withLock it }

        val refreshToken = Storage.get(StorageKeys.REFRESH_TOKEN) ?: return@withLock null
        try {
            // Whatever happens to the underlying engine, the mutex is
            // released by this deadline.
            withTimeout(REFRESH_TIMEOUT_MS) {
                val refreshClient = createDefault()
                val response = try {
                    refreshClient.post(AppConfig.getApiUrl("v1/auth/refresh")) {
                        contentType(ContentType.Application.Json)
                        setBody(RefreshTokenRequest(refreshToken))
                    }
                } finally {
                    refreshClient.close()
                }

                // A definitive 401/403 means this refresh token is dead
                // (revoked/expired) — every retry is doomed, so stop hammering
                // the endpoint and go through the normal logout path: clear the
                // session and reload, exactly what the sign-out flow does
                // (the reload rebuilds the app logged-out).
                if (response.status == HttpStatusCode.Unauthorized ||
                    response.status == HttpStatusCode.Forbidden
                ) {
                    logger.debug("Refresh token rejected by backend — clearing session")
                    Storage.clearSession()
                    kotlinx.browser.window.location.reload()
                    return@withTimeout null
                }

                val apiResponse = response.body<ApiResponse<RefreshTokenResponse>>()
                apiResponse.data?.let {
                    Storage.save(StorageKeys.ACCESS_TOKEN, it.accessToken)
                    Storage.save(StorageKeys.REFRESH_TOKEN, it.refreshToken)
                    logger.debug("Token refreshed successfully")
                    it.accessToken
                }
            }
        } catch (e: TimeoutCancellationException) {
            // Transient — keep the tokens so a later request can retry.
            logger.debug("Token refresh timed out")
            null
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            // Network error — do NOT clear the session: an offline user
            // must not be logged out.
            logger.debug("Token refresh failed: ${e.message}")
            null
        }
    }

    private fun cacheBuster(): String = js("Date.now().toString()") as String

    private fun HttpClientConfig<*>.installLogging() {
        if (AppConfig.environment == Environment.DEVELOPMENT) {
            install(Logging) {
                level = LogLevel.ALL
                logger = object : io.ktor.client.plugins.logging.Logger {
                    override fun log(message: String) {
                        this@HttpClientFactory.logger.debug(message)
                    }
                }
            }
        }
    }
}

fun HttpRequestBuilder.addDefaultHeaders() {
    header("Content-Type", "application/json")
    header("Accept", "application/json")
    header(PLATFORM_HEADER, PLATFORM_VALUE)
}

fun HttpRequestBuilder.addPlatformHeader() {
    header(PLATFORM_HEADER, PLATFORM_VALUE)
}

fun HttpRequestBuilder.addAuthHeader(token: String) {
    header("Authorization", "Bearer $token")
}

fun HttpRequestBuilder.setBaseUrl(baseUrl: String) {
    val currentUrl = url.buildString()
    if (!currentUrl.startsWith(baseUrl)) {
        url(baseUrl)
    }
}
